import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  makeStyles,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@material-ui/core';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { toast } from 'react-toastify';
import usePushSubscription from '../../hooks/usePushSubscription';
import { usePlanPermissions } from '../../context/PlanPermissionsContext';

const useStyles = makeStyles((theme) => ({
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    marginBottom: theme.spacing(2),
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
  notSupported: {
    color: theme.palette.warning.main,
    marginTop: theme.spacing(1),
  },
  button: {
    marginTop: theme.spacing(1),
  },
}));

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const NotificationModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { planActive } = usePlanPermissions();
  const { isSupported, isSubscribed, subscribe, unsubscribe, loading, error, permission } =
    usePushSubscription();
  const iosDevice = typeof navigator !== 'undefined' && isIOS();

  const handleToggle = async () => {
    if (loading) return;
    if (isSubscribed) {
      await unsubscribe();
      toast.info('Notificações desativadas.');
    } else {
      const ok = await subscribe();
      if (ok)
        toast.success('Notificações ativadas. Você receberá avisos de novos leads e mensagens.');
      else
        toast.error(
          error || 'Não foi possível ativar. Use HTTPS e permita notificações no navegador.'
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Notificações push</DialogTitle>
      <DialogContent>
        <Typography className={classes.subtitle}>
          Ative as notificações <strong>neste navegador</strong> (ou no celular) para receber avisos
        </Typography>

        {!isSupported ? (
          <Typography className={classes.notSupported} component="div">
            {iosDevice ? (
              <>
                No computador e no Android o push funciona no navegador normal (mesmo com a aba
                fechada). No iPhone e no iPad a <strong>Apple só permite</strong> notificações push
                quando o site é aberto pela Tela de Início (limitação da plataforma, não do site).
                <Box component="p" mt={1} mb={0}>
                  Para ativar no iOS: toque em <strong>Compartilhar</strong> (barra do Safari) e
                  escolha <strong>“Adicionar à Tela de Início”</strong>. Depois abra o site pelo
                  ícone na tela inicial e ative as notificações aqui.
                </Box>
              </>
            ) : (
              <>
                Não disponível neste dispositivo: use HTTPS e um navegador que suporte notificações
                (Chrome, Firefox, Edge ou Safari).
              </>
            )}
          </Typography>
        ) : (
          <>
            {!planActive && (
              <Typography color="error" className={classes.subtitle}>
                Regularize suas faturas para ativar notificações push.
              </Typography>
            )}
            <Box className={classes.status}>
              {permission === 'denied' && (
                <Typography color="error">
                  Notificações bloqueadas. Habilite nas configurações do navegador para este site.
                </Typography>
              )}
              {permission === 'granted' && (
                <>
                  {isSubscribed ? (
                    <NotificationsActiveIcon color="primary" fontSize="small" />
                  ) : (
                    <NotificationsOffIcon fontSize="small" />
                  )}
                  <span>{isSubscribed ? 'Notificações ativadas' : 'Notificações desativadas'}</span>
                </>
              )}
            </Box>

            <Button
              variant="contained"
              color={isSubscribed ? 'default' : 'primary'}
              onClick={handleToggle}
              disabled={loading || permission === 'denied' || !planActive}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              className={classes.button}
            >
              {loading
                ? 'Aguarde...'
                : isSubscribed
                  ? 'Desativar notificações'
                  : 'Ativar notificações'}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationModal;
